import { z } from "zod"
import { apiListSchema } from "./list"

// DTO Schemas
export const CreateEmailTemplateDtoSchema = z.object({
  Name: z.string().min(1),
  Subject: z.string().min(1).nullish(),
  HtmlContent: z.string().nullish(),
  TextContent: z.string().nullish(),
  Type: z.string().min(1),
})

export type CreateEmailTemplateDto = z.infer<typeof CreateEmailTemplateDtoSchema>

export const EmailTemplateDtoSchema = CreateEmailTemplateDtoSchema.extend({
  Id: z.number(),
  CreatedAt: z.string(),
  UpdatedAt: z.string(),
})

export type EmailTemplateDto = z.infer<typeof EmailTemplateDtoSchema>
export const emailTemplateDtoListSchema = apiListSchema(EmailTemplateDtoSchema)

// Model Schemas
export const CreateEmailTemplateSchema = z.object({
  name: z.string().min(1),
  subject: z.string().min(1),
  htmlContent: z.string(),
  textContent: z.string(),
  type: z.string().min(1),
})

export type CreateEmailTemplate = z.infer<typeof CreateEmailTemplateSchema>

export interface EmailTemplate {
  id: string
  name: string
  subject: string
  htmlContent: string
  textContent: string
  type: string
  createdAt: string
  updatedAt: string
}

// Mappers
export function createEmailTemplateToDto(template: CreateEmailTemplate): CreateEmailTemplateDto {
  return {
    Name: template.name,
    Subject: template.subject,
    HtmlContent: template.htmlContent,
    TextContent: template.textContent,
    Type: template.type,
  }
}

export function emailTemplateFromDto(dto: EmailTemplateDto): EmailTemplate {
  return {
    id: dto.Id.toString(),
    name: dto.Name,
    subject: dto.Subject ?? "",
    htmlContent: dto.HtmlContent ?? "",
    textContent: dto.TextContent ?? "",
    type: dto.Type,
    createdAt: dto.CreatedAt,
    updatedAt: dto.UpdatedAt,
  }
}

export function emailTemplateToDto(template: EmailTemplate): EmailTemplateDto {
  return {
    Id: Number.parseInt(template.id),
    Name: template.name,
    Subject: template.subject,
    HtmlContent: template.htmlContent,
    TextContent: template.textContent,
    Type: template.type,
    CreatedAt: template.createdAt,
    UpdatedAt: template.updatedAt,
  }
}

// Entity types for templates
export const ENTITY_TYPES = [
  { value: "Accommodation", label: "Accommodation" },
  { value: "ReservationService", label: "Service" },
]

// Template types - focused on specific use cases including declines
export const TEMPLATE_TYPES = {
  Accommodation: [
    { value: "hotel-booking-request", label: "Hotel Booking Request" },
    { value: "hotel-booking-confirmation", label: "Hotel Booking Confirmation" },
    { value: "hotel-booking-declined", label: "Hotel Booking Declined" },
  ],
  "ReservationService": [
    { value: "service-request", label: "Service Request" },
    { value: "service-confirmation", label: "Service Confirmation" },
    { value: "service-declined", label: "Service Declined" },
  ],
}

// Fields available for each entity type
export const ENTITY_FIELDS = {
  Accommodation: [
    // Hotel fields
    { name: "Accomodation.HotelRoom.Hotel.Name", label: "Hotel Name" },
    { name: "Accomodation.HotelRoom.Hotel.Email", label: "Hotel Email" },
    { name: "Accomodation.HotelRoom.Hotel.Address", label: "Hotel Address" },
    { name: "Accomodation.HotelRoom.Hotel.Locality", label: "Hotel Locality" },
    { name: "Accomodation.HotelRoom.Hotel.PhoneNumber", label: "Hotel Phone Number" },
    { name: "Accomodation.HotelRoom.Hotel.CheckInTime", label: "Check-in Time" },
    { name: "Accomodation.HotelRoom.Hotel.CheckOutTime", label: "Check-out Time" },
    { name: "Accomodation.HotelRoom.Hotel.BreakfastStartTime", label: "Breakfast Start Time" },
    { name: "Accomodation.HotelRoom.Hotel.BreakfastEndTime", label: "Breakfast End Time" },

    // Hotel Room fields
    { name: "Accomodation.HotelRoom.Designation", label: "Room Type" },
    { name: "Accomodation.HotelRoom.Notes", label: "Room Notes" },

    // Accommodation fields
    { name: "Accomodation.RequestedType", label: "Requested Room Type" },
    { name: "Accomodation.HeadCount", label: "Number of People" },
    { name: "Accomodation.ChildrenCount", label: "Number of Children" },
    { name: "Accomodation.NumberOfBeds", label: "Number of Beds" },
    { name: "Accomodation.Price", label: "Price" },
    { name: "Accomodation.Day.CheckInDate", label: "Check-in Date" },
    { name: "Accomodation.CheckOutDate", label: "Check-out Date" },
    { name: "Accomodation.Notes", label: "Accommodation Notes" },
    { name: "Accomodation.Status", label: "Accommodation State" },

    // Reservation fields
    { name: "Accomodation.Reservation.Begin", label: "Reservation Start Date" },
    { name: "Accomodation.Reservation.End", label: "Reservation End Date" },
    { name: "Accomodation.Reservation.State", label: "Reservation Status" },
    { name: "Accomodation.Reservation.Worker.Email", label: "Reservation Worker Email" },

    // Client fields
    // { name: "client.name", label: "Client Name" },
    // { name: "client.email", label: "Client Email" },
    // { name: "client.phone", label: "Client Phone" },
    // { name: "client.country", label: "Client Country" },
    // { name: "client.language", label: "Client Language" },

    // Fulfillment fields
    { name: "Accommodation.Fulfillment.InvoiceNumber", label: "Invoice Number" },
    { name: "Accommodation.Fulfillment.Value", label: "Value" },
    { name: "Accommodation.Fulfillment.DueDate", label: "Due Date" },

    // RSVP fields - using the fixed URL pattern
    // { name: "rsvp.confirmUrl", label: "Confirm URL" },
    // { name: "rsvp.declineUrl", label: "Decline URL" },
    // { name: "rsvp.updateUrl", label: "Update URL" },
  ],
  "ReservationService": [
    // Service fields
    { name: "ReservationService.Service.Name", label: "Service Name" },
    { name: "ReservationService.Service.Type", label: "Service Type" },
    { name: "ReservationService.Service.Description", label: "Service Description" },

    // Service Provider fields
    { name: "ReservationService.ServiceProvider.Person.Name", label: "Provider Name" },
    { name: "ReservationService.ServiceProvider.Person.Email", label: "Provider Email" },
    { name: "ReservationService.ServiceProvider.Person.Phone", label: "Provider Phone" },

    // Reservation Service fields
    { name: "ReservationService.StartHour", label: "Service Start Time" },
    { name: "ReservationService.EndHour", label: "Service End Time" },
    { name: "ReservationService.Price", label: "Service Price" },
    { name: "ReservationService.State", label: "Service State" },
    { name: "ReservationService.Status", label: "Service State" },
    { name: "ReservationService.Notes", label: "Service Notes" },
    { name: "ReservationService.ClientNotes", label: "Client Notes" },
    { name: "ReservationService.Date", label: "Service Date" },
    { name: "ReservationService.HeadCount", label: "Number of People" },

    // Reservation fields
    { name: "ReservationService.Reservation.Begin", label: "Reservation Start Date" },
    { name: "ReservationService.Reservation.End", label: "Reservation End Date" },
    { name: "ReservationService.Reservation.State", label: "Reservation Status" },
    { name: "ReservationService.Reservation.Worker.Email", label: "Reservation Worker Email" },

    // Client fields
    // { name: "ReservationService.ReservationServiceClient.Client.Name", label: "Client Name" },
    // { name: "ReservationService.ReservationServiceClient.Client.Email", label: "Client Email" },
    // { name: "ReservationService.ReservationServiceClient.Client.Phone", label: "Client Phone" },
    // { name: "ReservationService.ReservationServiceClient.Client.Country", label: "Client Country" },
    // { name: "ReservationService.ReservationServiceClient.Client.Language", label: "Client Language" },

    // Fulfillment fields
    { name: "ReservationService.FulfillmentReservationService.InvoiceNumber", label: "Invoice Number" },
    { name: "ReservationService.FulfillmentReservationService.Value", label: "Value" },
    { name: "ReservationService.FulfillmentReservationService.DueDate", label: "Due Date" },

    // RSVP fields - using the fixed URL pattern
    // { name: "rsvp.confirmUrl", label: "Confirm URL" },
    // { name: "rsvp.declineUrl", label: "Decline URL" },
    // { name: "rsvp.updateUrl", label: "Update URL" },
    // { name: "rsvp.priceUpdateUrl", label: "Price Update URL" },
  ],
}

// Common fields available for all entity types
export const COMMON_FIELDS = [
  // { name: "date", label: "Current Date" },
  // { name: "companyName", label: "Company Name" },
  // { name: "companyEmail", label: "Company Email" },
  // { name: "companyPhone", label: "Company Phone" },
  // { name: "companyAddress", label: "Company Address" },
  // { name: "companyLogo", label: "Company Logo URL" },
]

// Trigger event fields
export const TRIGGER_EVENT_FIELDS = [
  // { name: "triggerEvent.id", label: "Trigger Event ID (for RSVP URLs)" },
  // { name: "triggerEvent.notes", label: "Response Notes" },
  // { name: "triggerEvent.status", label: "Response Status" },
  // { name: "triggerEvent.price", label: "Updated Price" },
  // { name: "BASE_URL", label: "Base URL (for internal links)" },
]
